// DCIC-07 Facility Status Update — create / edit / view page.
// update_type:
//   status_update (default)  — functional status (+ GPS, notes). This is the
//                              only mode R05 ever sees; the selector is hidden.
//   climate_assessment       — created by R04/R03, approved by R02. Every
//                              assessment belongs to the one facility chosen.
// assessment_outcome is set by the approval workflow and is read-only.
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import RecordWizard from '../../component/reusable/formRecords/RecordWizards';
import { Field, ReadonlyBox, SectionDivider, SectionHead, Select, INP, INP_LOCKED } from '../../component/reusable/formRecords/formFieldPrimitives';
import { SingleToggle } from '../../component/shared/forms/FormControls';
import GpsCoordinateField from '../../component/shared/GpsCoordinateField';
import FileUploadField from '../../component/shared/FileUploadField';
import RegistryCustomFields from '../../component/Admin/FoundationalTables/FormRegistry/RegistryCustomFields';
import { RegistryFormScope, useRegistryLabel } from '../../component/Admin/FoundationalTables/FormRegistry/registryFieldOverrides';
import { useAuth } from '../../context/AuthContext';
import { facilitiesApi, FUNCTIONAL_STATUS_OPTIONS } from '../../api/facilitiesApi';
import { institutionApi } from '../../api/institutionsApi';
import { workplanActivitiesApi } from '../../api/planningApi';
import { approvedClimateAssessments, facilityStatusUpdatesApi } from '../../api/facilityStatusUpdatesApi';
import { humanizeWorkflowStatus } from '../../component/Infrastructure/shared/FacilityWorkflow';
import {
  ASSESSMENT_OUTCOME_OPTIONS, ASSESSMENT_SCOPE_OPTIONS, ASSESSMENT_TYPE_OPTIONS, BIOGAS_SYSTEM_TYPE,
  EMISSIONS_CLASSIFICATION_OPTIONS, REPORT_REQUIRED_ASSESSMENT_TYPES, UPDATE_TYPE_OPTIONS,
  VERIFICATION_ASSESSMENT_TYPES, VERIFICATION_RESULT_OPTIONS, labelOf,
} from '../../component/Infrastructure/shared/climateOptions';

const MODULE_NAME = 'Infrastructure & Services';
const SUB_MODULES = [
  { value: 'core', label: 'Facility Core Registry' },
  { value: 'status', label: 'Facility Status Update' },
];
const TAB_ROUTE = '/infrastructure/status';
const CLIMATE_CREATORS = ['R01', 'R03', 'R04'];
const STATUS_CREATORS = ['R01', 'R03', 'R04', 'R05'];

const EMPTY = {
  facility_uuid: '', update_type: 'status_update',
  functional_status: '', gps_coordinates: '', notes: '',
  assessment_type: '', assessment_scope: '', assessment_date: '', assessing_institution_uuid: '',
  linked_activity_uuid: '', verification_result: '', emissions_classification: '', baseline_reference_uuid: '',
  assessment_report: '', assessment_report_file_name: '',
};

const today = () => new Date().toISOString().slice(0, 10);

function ReviewRow({ label: pageLabel, value }) {
  const label = useRegistryLabel(pageLabel);
  return (
    <div className="flex items-center justify-between border-b border-gray-50 py-2 last:border-0">
      <span className="text-xs font-semibold text-gray-500">{label}</span>
      <span className="text-right text-sm text-gray-900">{value || value === 0 ? String(value) : '—'}</span>
    </div>
  );
}

function useOptions(loader, deps = []) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let active = true;
    loader().then((rows) => { if (active) setItems(rows); }).catch(() => { if (active) setItems([]); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return items;
}

const listOf = (result) => (Array.isArray(result?.items) ? result.items : (Array.isArray(result) ? result : []));

export default function FacilityStatusUpdateRecord({ viewOnly = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { uuid } = useParams();
  const isEditMode = Boolean(uuid);
  const [, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const roleId = user?.roleId ?? 'R06';
  const isR05 = roleId === 'R05';
  const canCreateClimate = CLIMATE_CREATORS.includes(roleId);

  const [form, setForm] = useState(EMPTY);
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(isEditMode);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const facilities = useOptions(() => facilitiesApi.listAll({ per_page: 500 }).then(listOf));
  const institutions = useOptions(() => institutionApi.list({ per_page: 200 }).then(listOf));
  const activities = useOptions(() => workplanActivitiesApi.listAll({ per_page: 500 }).then(listOf));

  useEffect(() => {
    if (!isEditMode) return;
    const seed = location.state?.row;
    const apply = (row) => {
      setRecord(row);
      setForm(Object.fromEntries(Object.keys(EMPTY).map((key) => [key, row?.[key] ?? EMPTY[key]])));
    };
    if (seed && seed.uuid === uuid) { apply(seed); setLoading(false); return; }
    facilityStatusUpdatesApi.get(uuid)
      .then(apply)
      .catch((e) => setError(e?.message ?? 'Failed to load record.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, uuid]);

  const currentStatus = String(record?.data_status ?? 'draft').toLowerCase();
  const isClimate = form.update_type === 'climate_assessment';
  const canEditRecord = !isEditMode || (
    ['draft', 'rejected'].includes(currentStatus) && (
      roleId === 'R01'
      || (isClimate ? CLIMATE_CREATORS.includes(roleId) : STATUS_CREATORS.includes(roleId))
    )
  );
  const locked = isEditMode && !canEditRecord;
  const fieldCls = locked ? INP_LOCKED : INP;

  const facilityOptions = useMemo(() => facilities.map((f) => ({
    value: f.uuid ?? f.id,
    label: [f.name, f.facilityType, f.lga].filter(Boolean).join(' — '),
  })), [facilities]);
  const selectedFacility = facilities.find((f) => (f.uuid ?? f.id) === form.facility_uuid) ?? null;
  const institutionOptions = institutions.map((i) => ({ value: i.uuid ?? i.id, label: i.name ?? i.institution_name ?? i.uuid }));
  const activityOptions = activities.map((a) => ({ value: a.uuid ?? a.id, label: [a.activityTitle ?? a.title, a.workplan].filter(Boolean).join(' — ') }));
  const baselineOptions = useMemo(() => approvedClimateAssessments({ assessmentType: 'ghg_baseline' })
    .filter((row) => row.uuid !== uuid)
    .sort((a, b) => Number(b.facility_uuid === form.facility_uuid) - Number(a.facility_uuid === form.facility_uuid))
    .map((row) => ({ value: row.uuid, label: [row.facility_name, row.assessment_date, labelOf(ASSESSMENT_SCOPE_OPTIONS, row.assessment_scope)].filter(Boolean).join(' — ') })),
  [form.facility_uuid, uuid]);

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const isVerification = VERIFICATION_ASSESSMENT_TYPES.includes(form.assessment_type);
  const isScreening = form.assessment_type === 'emissions_screening';
  const reportRequired = REPORT_REQUIRED_ASSESSMENT_TYPES.includes(form.assessment_type);

  const stepValid = locked || (Boolean(form.facility_uuid) && (isClimate
    ? Boolean(form.assessment_type && form.assessment_scope && form.assessment_date && form.assessment_date <= today() && form.assessing_institution_uuid)
      && (!isVerification || Boolean(form.verification_result))
      && (!isScreening || Boolean(form.emissions_classification && form.baseline_reference_uuid))
      && (!reportRequired || Boolean(form.assessment_report))
    : Boolean(form.functional_status)));
  const outcomeLabel = labelOf(ASSESSMENT_OUTCOME_OPTIONS, record?.assessment_outcome ?? 'pending_approval');

  const switchSubModule = (value) => {
    if (value === 'status') return;
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('subModule', value); return next; }, { replace: true });
  };

  const handleSave = async () => {
    if (saving || locked) return;
    setSaving(true);
    setError('');
    const payload = {
      ...form,
      update_type: isR05 ? 'status_update' : form.update_type,
      facility_name: selectedFacility?.name ?? record?.facility_name ?? '',
      facility_type: selectedFacility?.facility_type ?? record?.facility_type ?? '',
      state: selectedFacility?.state ?? record?.state ?? '',
      lga: selectedFacility?.lga ?? record?.lga ?? '',
      assessing_institution_name: institutionOptions.find((o) => o.value === form.assessing_institution_uuid)?.label ?? record?.assessing_institution_name ?? '',
      linked_activity_title: activityOptions.find((o) => o.value === form.linked_activity_uuid)?.label ?? record?.linked_activity_title ?? '',
    };
    try {
      if (isEditMode) await facilityStatusUpdatesApi.update(uuid, payload);
      else await facilityStatusUpdatesApi.create(payload, user);
      navigate(TAB_ROUTE);
    } catch (e) {
      setError(e?.message ?? 'Failed to save the facility status update.');
      setSaving(false);
    }
  };

  const lookupLabel = (options, value) => options.find((o) => o.value === value)?.label ?? '';

  function renderReview() {
    return (
      <>
        <SectionHead title="Facility Status Update" />
        <ReviewRow label="Facility" value={lookupLabel(facilityOptions, form.facility_uuid) || record?.facility_name} />
        <ReviewRow label="Update Type" value={labelOf(UPDATE_TYPE_OPTIONS, form.update_type)} />
        <ReviewRow label="Functional Status" value={labelOf(FUNCTIONAL_STATUS_OPTIONS, form.functional_status)} />
        <ReviewRow label="GPS Coordinates" value={form.gps_coordinates} />
        <ReviewRow label="Notes" value={form.notes} />
        {isClimate && (
          <>
            <SectionHead title="Climate Assessment" />
            <ReviewRow label="Assessment Type" value={labelOf(ASSESSMENT_TYPE_OPTIONS, form.assessment_type)} />
            <ReviewRow label="Assessment Scope" value={labelOf(ASSESSMENT_SCOPE_OPTIONS, form.assessment_scope)} />
            <ReviewRow label="Assessment Date" value={form.assessment_date} />
            <ReviewRow label="Assessing Institution" value={lookupLabel(institutionOptions, form.assessing_institution_uuid) || record?.assessing_institution_name} />
            <ReviewRow label="Linked Activity" value={lookupLabel(activityOptions, form.linked_activity_uuid) || record?.linked_activity_title} />
            {isVerification && <ReviewRow label="Verification Result" value={labelOf(VERIFICATION_RESULT_OPTIONS, form.verification_result)} />}
            {isScreening && <ReviewRow label="Emissions Classification" value={labelOf(EMISSIONS_CLASSIFICATION_OPTIONS, form.emissions_classification)} />}
            {isScreening && <ReviewRow label="Baseline Reference" value={lookupLabel(baselineOptions, form.baseline_reference_uuid)} />}
            <ReviewRow label="Assessment Outcome" value={outcomeLabel} />
            <ReviewRow label="Assessment Report" value={form.assessment_report ? (form.assessment_report_file_name || 'File attached') : ''} />
          </>
        )}
        <SectionHead title="System" />
        <ReviewRow label="Record Status" value={isEditMode ? humanizeWorkflowStatus(currentStatus) : 'Draft (system-set)'} />
        {record?.rejection_notes && <ReviewRow label="Rejection Notes" value={record.rejection_notes} />}
      </>
    );
  }

  function renderForm() {
    return (
      <div className="space-y-5">
        <SectionHead title="Select Module" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Module" required>
            <Select value={MODULE_NAME} disabled className={INP_LOCKED} options={[{ value: MODULE_NAME, label: MODULE_NAME }]} />
          </Field>
          <Field label="Sub Module" required>
            <Select value="status" onChange={(e) => switchSubModule(e.target.value)} options={isR05 ? SUB_MODULES.filter((o) => o.value !== 'core') : SUB_MODULES} disabled={isEditMode || isR05} className={isEditMode || isR05 ? INP_LOCKED : INP} />
          </Field>
        </div>
        <SectionDivider />

        <SectionHead title="Facility Status Update" />
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Field label="Facility" required hint="Select an existing facility — this form cannot create one. Every assessment is tied to this facility.">
              <Select value={form.facility_uuid} onChange={set('facility_uuid')} options={facilityOptions} disabled={locked} placeholder="Select from registry" className={fieldCls} />
            </Field>
          </div>
          {!isR05 && canCreateClimate && (
            <div className="col-span-2">
              <Field label="Update Type" required hint="Status Update keeps the facility's functional status current; Climate Assessment opens the climate section below.">
                {locked || isEditMode
                  ? <ReadonlyBox>{labelOf(UPDATE_TYPE_OPTIONS, form.update_type)}</ReadonlyBox>
                  : <SingleToggle value={form.update_type} onChange={(value) => value && setForm((prev) => ({ ...prev, update_type: value }))} options={UPDATE_TYPE_OPTIONS} />}
              </Field>
            </div>
          )}
          <Field
            label="Functional Status"
            required={!isClimate}
            hint={[
              isClimate ? 'Optional for a climate assessment.' : null,
              selectedFacility?.facility_type === BIOGAS_SYSTEM_TYPE ? 'For a biogas system, Active means operational.' : null,
            ].filter(Boolean).join(' ') || undefined}
          >
            <Select value={form.functional_status} onChange={set('functional_status')} options={FUNCTIONAL_STATUS_OPTIONS} disabled={locked} placeholder="Select status" className={fieldCls} />
          </Field>
          <div className="col-span-2">
            <GpsCoordinateField label="GPS Coordinates" helperText="Optional — confirm location" value={form.gps_coordinates} onChange={set('gps_coordinates')} disabled={locked} />
          </div>
          <div className="col-span-2">
            <Field label="Notes">
              <textarea rows={3} maxLength={500} value={form.notes} onChange={set('notes')} disabled={locked} placeholder="Optional notes" className={`${fieldCls} resize-none`} />
            </Field>
          </div>
        </div>

        {isClimate && (
          <div className="space-y-4 rounded-2xl border border-dashed border-green-300 bg-green-50/40 p-4">
            <SectionHead title="Climate Assessment" />
            <p className="text-xs text-green-700">Conditional section — shown for a climate assessment. Record the assessor's result for this facility; emissions are not calculated here.</p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Assessment Type" required>
                <Select value={form.assessment_type} onChange={set('assessment_type')} options={ASSESSMENT_TYPE_OPTIONS} disabled={locked} placeholder="Select assessment type" className={fieldCls} />
              </Field>
              <Field label="Assessment Scope" required hint="State Baseline marks this record as part of the State GHG baseline (State taken from the facility location).">
                <Select value={form.assessment_scope} onChange={set('assessment_scope')} options={ASSESSMENT_SCOPE_OPTIONS} disabled={locked} placeholder="Select scope" className={fieldCls} />
              </Field>
              <Field label="Assessment Date" required hint="Cannot be in the future.">
                <input type="date" max={today()} value={form.assessment_date} onChange={set('assessment_date')} disabled={locked} className={fieldCls} />
              </Field>
              <Field label="Assessing Institution" required hint="An institution, not a person.">
                <Select value={form.assessing_institution_uuid} onChange={set('assessing_institution_uuid')} options={institutionOptions} disabled={locked} placeholder="Select institution" className={fieldCls} />
              </Field>
              <div className="col-span-2">
                <Field label="Linked Activity" hint="Optional — set when the assessment is for a planned intervention.">
                  <Select value={form.linked_activity_uuid} onChange={set('linked_activity_uuid')} options={activityOptions} disabled={locked} placeholder="Select activity (optional)" className={fieldCls} />
                </Field>
              </div>
              {isVerification && (
                <Field label="Verification Result" required hint="On approval it sets the facility's verification status.">
                  <Select value={form.verification_result} onChange={set('verification_result')} options={VERIFICATION_RESULT_OPTIONS} disabled={locked} placeholder="Select result" className={fieldCls} />
                </Field>
              )}
              {isScreening && (
                <>
                  <Field label="Emissions Classification" required hint="Assigned by the assessor — never calculated here.">
                    <Select value={form.emissions_classification} onChange={set('emissions_classification')} options={EMISSIONS_CLASSIFICATION_OPTIONS} disabled={locked} placeholder="Select classification" className={fieldCls} />
                  </Field>
                  <Field label="Baseline Reference" required hint="An approved GHG baseline assessment.">
                    <Select value={form.baseline_reference_uuid} onChange={set('baseline_reference_uuid')} options={baselineOptions} disabled={locked || baselineOptions.length === 0}
                      placeholder={baselineOptions.length ? 'Select approved baseline' : 'No approved GHG baseline yet'} className={fieldCls} />
                  </Field>
                </>
              )}
              <Field label="Assessment Outcome" hint="Set by the approval workflow — only approved assessments count.">
                <ReadonlyBox>{outcomeLabel}</ReadonlyBox>
              </Field>
              <div className="col-span-2">
                <FileUploadField
                  label="Assessment Report"
                  required={reportRequired}
                  value={form.assessment_report}
                  fileName={form.assessment_report_file_name}
                  onChange={(dataUri, name) => setForm((prev) => ({ ...prev, assessment_report: dataUri, assessment_report_file_name: name }))}
                  accept=".pdf"
                  maxSizeMB={10}
                  disabled={locked}
                  hint={reportRequired ? 'Required for GHG baselines and emissions screening — PDF, max 10MB.' : 'Optional — PDF, max 10MB.'}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (viewOnly) {
    return (
      <div className="min-h-screen bg-white p-6">
        {error && <div className="mx-auto mb-4 max-w-4xl rounded-lg border border-red-100 bg-red-50 px-4 py-2.5 text-xs text-red-700">{error}</div>}
        <div className="mx-auto max-w-4xl rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-base font-bold uppercase tracking-wide text-gray-900">Record Details</h2>
              <p className="mt-1 text-sm text-gray-400">Review the information for this facility update</p>
            </div>
            <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold capitalize text-gray-600">{currentStatus}</span>
          </div>
          <div className="space-y-4">{loading ? <p className="text-sm text-gray-400">Loading record…</p> : renderReview()}</div>
          <div className="mt-6 flex gap-3 border-t border-gray-100 pt-6">
            <button type="button" onClick={() => navigate(TAB_ROUTE)} className="h-11 flex-1 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">Back</button>
            {canEditRecord && record && (
              <button type="button" onClick={() => navigate(`/infrastructure/edit/${uuid}?subModule=status`, { state: { row: record } })}
                className="h-11 flex-1 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700">Edit Record</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const isLastStep = step === 1;
  return (
    <div className="min-h-screen bg-white p-6">
      {loading && <div className="mx-auto mb-4 max-w-4xl rounded-lg border border-amber-100 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700">Loading record…</div>}
      {locked && (
        <div className="mx-auto mb-4 max-w-4xl rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
          This record is <strong>{currentStatus}</strong> — field values are locked for your role.
        </div>
      )}
      <RegistryFormScope moduleKey="infrastructure" submoduleKey="status">
        <RecordWizard
          steps={[{ key: 'update', label: 'Facility Update' }, { key: 'review', label: 'Review' }]}
          currentStep={step}
          title={isEditMode ? 'Edit Record' : 'Add New Record'}
          subtitle={isEditMode ? 'Update the details for this record' : 'Fill in the details to create a new record'}
          statusLabel={isEditMode ? currentStatus : 'draft'}
          error={error}
          onCancel={() => navigate(TAB_ROUTE)}
          onBack={() => setStep(0)}
          onNext={() => setStep(1)}
          canGoNext={stepValid}
          nextLabel="Review"
          footer={isLastStep ? (
            <>
              <button type="button" onClick={() => setStep(0)} className="h-11 flex-1 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">Previous</button>
              <button type="button" onClick={handleSave} disabled={saving || locked}
                className="h-11 flex-1 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-40">
                {saving ? 'Saving…' : (isEditMode ? 'Save Changes' : 'Create')}
              </button>
            </>
          ) : undefined}
        >
          <RegistryCustomFields moduleKey="infrastructure" submoduleKey="status" />
          {isLastStep ? renderReview() : renderForm()}
        </RecordWizard>
      </RegistryFormScope>
    </div>
  );
}
